import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react'
import { FormikProps } from 'formik'
import { useIntl } from 'react-intl'
import { mdiPlus, mdiAlert } from '@mdi/js'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { generatePath, useHistory } from 'react-router'
import { unwrapResult } from '@reduxjs/toolkit'

import NetworkWizardLayout from '../NetworkWizardLayout'
import {
  PurchaseSubscriptionBlock,
  PurchaseSubscriptionMainBlock,
  StyledAddCardWrapper,
  StyledListCardWrapper,
  SectionLabel,
  CheckoutBlockContainer,
  CheckoutBlockWrapper,
  PurchaseStepBottomPanelContainer,
  PurchaseStepBottomPanelBtns,
} from './styles'
import { purchaseSubscriptionMessages } from './messages'
import { formatPrice } from 'features/home/components/HomeSubscription/utils'
import {
  NetworkWizardBottomPanel,
  NetworkWizardCancelButton,
  NetworkWizardSubmitButton,
  NetworkWizardTitle,
} from '../styles'
import { commonMessages } from 'app/i18n/messages'
import { SpinnerCentered } from 'components/Loader'
import {
  detachPaymentMethodSelectors,
  hasNoPaymentMethodsSelector,
  paymentMethodsListSelector,
  selectedPaymentMethodIdSelector,
} from 'features/paymentMethods/selectors'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { WhiteBlock } from 'components/Layout'
import AddPaymentMethodForm, {
  AddPaymentMethodDetails,
} from 'features/home/components/HomeSubscription/SubscriptionManagement/AddPaymentMethodForm'
import theme from 'theme_'
import { STRIPE_PK } from 'app/consts'
import elementsLocaleMapper from 'app/utils/elementsLocaleMapper'
import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import { getPaymentMethodsListThunk } from 'features/paymentMethods/slice'
import { StyledTotalToPay } from 'components/SubsManageBottomPanel/styles'
import ROUTES from 'app/routes'
import {
  useCurrentHomeSiteId,
  useNetworkHomeJobsRouteParams,
} from 'features/networkAi/hooks'
import { PurchaseSubscriptionState } from './types'
import PaymentMethodsCardsList from 'features/paymentMethods/components/PaymentMethodsCardsList'
import CheckoutInfo from 'features/home/components/HomeSubscription/SubscriptionManagement/CheckoutInfo'
import {
  fetchSubscriptionPricesSliceThunk,
  fetchSubscriptionPeriodEndSliceSliceThunk,
} from 'features/home/components/HomeSubscription/SubscriptionDetails/slice'
import { CommonLinkButton } from 'components/Button'
import { MdiIcon } from 'components/Icons'
import { createSetupIntentThunk } from 'features/home/components/HomeSubscription/slice'
import {
  setupIntentSelector,
  upcomingInvoiceSelectors,
} from 'features/home/components/HomeSubscription/selectors'
import {
  subscribedAccessPointsSelector,
  subscribedEssentialsSelector,
} from 'features/networkAi/selectors'

//import PurchaseDeviceTotal from 'features/home/components/HomeSubscription/SubscriptionRenewals/PurchaseDeviceTotal'
import PurchaseDeviceTotal from './PurchaseDeviceTotal'

import AddDeviceStep from './Steps/AddDevicesStep'
import {
  fetchHomeThunk,
  purchaseRequestSelectors,
  subscriptionApDevicePriceSelector,
  subscriptionPricesSelector,
} from 'features/home'
import { purchaseSubscriptionThunk } from 'features/home/components/HomeSubscription/SubscriptionManagement/UpgradeSubscription/slice'
import { NetworkWizardContext } from '../NetworkWizardContext'
import PurchaseState from './PurchaseState'
import PurchaseStateSuccess from './PurchaseStateSuccess'
import { fetchClaimDevicesThunk } from 'features/networkAi/slice'
import { subsribedAccessPointsCountSelector } from 'features/networkAi/selectors'
import {
  subscriptionEssentialsPriceSelector,
  subscriptionPeriodEndSelector,
  subscriptionsCreditSelector,
} from 'features/home'
import { selectedHomeSelector } from 'features/home/selectors'
import { isExpiringSoon } from 'features/home/components/HomeSubscription/utils'
import { getHomeSubscriptonInfo } from 'features/home/utils'
import { DateTime } from 'luxon'
import { fetchSubscriptionCreditsThunk } from 'features/home/components/HomeSubscription/slice'
import { SubscriptionCreditProduct } from 'features/home/types'

const SUBSCRIPTION_STATE = {
  NONE: 'NONE',
  ENROLLED: 'ENROLLED',
  WILL_NOT_RENEW: 'WILL_NOT_RENEW',
  EXPIRES: 'EXPIRES',
}

function PurchaseDeviceSubscription() {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const { rootDomainId, jobId, homeId } = useNetworkHomeJobsRouteParams()
  const siteId = useCurrentHomeSiteId({ homeId })
  const home = useAppSelector(selectedHomeSelector.data)
  const subscriptionInfo = getHomeSubscriptonInfo(home)
  const [isAcceptPrivacy, setIsAcceptPrivacy] = useState(false)
  const { job } = useContext(NetworkWizardContext)
  const [essentialsCredits, setEssentialsCredits] = useState(0)
  const [apCredits, setAPCredits] = useState(0)

  const subscriptionState = useMemo(() => {
    if (subscriptionInfo) {
      if (subscriptionInfo.autoRenew) return SUBSCRIPTION_STATE.ENROLLED
      return isExpiringSoon(subscriptionInfo, DateTime.now())
        ? SUBSCRIPTION_STATE.EXPIRES
        : SUBSCRIPTION_STATE.WILL_NOT_RENEW
    }
    return SUBSCRIPTION_STATE.NONE
  }, [subscriptionInfo])

  const subscribedCount = useAppSelector(subsribedAccessPointsCountSelector)
  //const [previewPrice,setPreviewPrice] = useState({amount_due:0})

  const subscribedAccessPoints = useAppSelector(subscribedAccessPointsSelector)
  const subscribedEssentials = useAppSelector(subscribedEssentialsSelector)
  const essentialsPrice = useAppSelector(
    subscriptionEssentialsPriceSelector(subscribedEssentials?.hostTier)
  )
  //const renewingAP = useAppSelector(networkSubscriptionStatusSelector(subscribedAccessPoints.count))
  const renewDate = useAppSelector(subscriptionPeriodEndSelector.data)
  const creditAmounts = useAppSelector(subscriptionsCreditSelector.data)
  const creditAmountsLoading = useAppSelector(
    subscriptionsCreditSelector.isFetching
  )

  const renewalDate = useMemo(() => {
    if (renewDate !== null) {
      if (
        subscribedAccessPoints.autoRenew === null ||
        subscribedAccessPoints.autoRenew === undefined
      ) {
        return [
          new Date().setFullYear(new Date().getFullYear() + 1),
          renewDate.data,
        ]
      } else {
        return [subscribedAccessPoints.autoRenew, renewDate.data]
      }
    } else {
      if (
        subscribedAccessPoints.autoRenew === null ||
        subscribedAccessPoints.autoRenew === undefined
      ) {
        return [
          new Date().setFullYear(new Date().getFullYear() + 1),
          new Date().setFullYear(new Date().getFullYear() + 1),
        ]
      } else {
        return [
          subscribedAccessPoints.autoRenew,
          subscribedAccessPoints.autoRenew,
        ]
      }
    }
  }, [subscribedAccessPoints, renewDate])

  useEffect(() => {
    const params = { after: renewalDate[1] }
    dispatch(fetchSubscriptionCreditsThunk(homeId, params))
  }, [dispatch, homeId, renewalDate])

  useEffect(() => {
    if (
      creditAmounts !== undefined &&
      creditAmounts?.products.length !== undefined &&
      creditAmounts?.products.length > 0
    ) {
      creditAmounts?.products.forEach(function (
        product: SubscriptionCreditProduct
      ) {
        if (product.productName === 'Savant Essentials') {
          setEssentialsCredits(product.amount)
        } else if (product.productName === 'Smart Network') {
          setAPCredits(product.amount)
        }
      })
    } else {
      setEssentialsCredits(0)
      setAPCredits(0)
    }
  }, [creditAmounts])

  const { formatMessage } = useIntl()

  const [apEnabled, setApEnabled] = useState(true)
  const [networkAiEnabled, setNetworkAiEnabled] = useState(true)
  const [amountDue, setAmountDue] = useState(0)
  const [totalAmountDue, setTotalAmountDue] = useState(0)
  const [apQuantity, setApQuantity] = useState(
    subscribedCount > 1 ? subscribedCount : 1
  )
  const [isShowLoader, setIsShowLoader] = useState(false)

  const paymentMethodFormRef =
    useRef<FormikProps<AddPaymentMethodDetails>>(null)

  useEffect(() => {
    dispatch(fetchSubscriptionPricesSliceThunk(homeId))
    dispatch(fetchSubscriptionPeriodEndSliceSliceThunk(homeId))
  }, [dispatch, homeId])

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (rootDomainId) {
        await dispatch(getPaymentMethodsListThunk(rootDomainId))
      }
    }
    fetchPaymentMethods()
  }, [dispatch, rootDomainId])

  const apPrice = useAppSelector(subscriptionApDevicePriceSelector)

  const hasPaymentMethods = !useAppSelector(hasNoPaymentMethodsSelector)
  const selectedPaymentMethod = useAppSelector(selectedPaymentMethodIdSelector)
  const upcomingInvoice = useAppSelector(upcomingInvoiceSelectors.data)
  const purchaseRequestData = useAppSelector(purchaseRequestSelectors.data)

  const isPurchaseRequestLoading = useAppSelector(
    purchaseRequestSelectors.isFetching
  )
  const isPaymentMethodDetaching = useAppSelector(
    detachPaymentMethodSelectors.isFetching
  )

  const [purchaseState, setPurchaseState] = useState<PurchaseSubscriptionState>(
    PurchaseSubscriptionState.addDevice
  )

  const [prevPurchaseState, setPrevPurchaseState] =
    useState<PurchaseSubscriptionState>(PurchaseSubscriptionState.addDevice)

  const isAddPaymentMethodState =
    purchaseState === PurchaseSubscriptionState.addPaymentMethod

  const isSuccessSubcription =
    purchaseState === PurchaseSubscriptionState.success

  const isShowTotal =
    [
      PurchaseSubscriptionState.error,
      PurchaseSubscriptionState.success,
    ].includes(purchaseState) === false

  const handlePaymentMethodAdd = () => {
    rootDomainId && dispatch(getPaymentMethodsListThunk(rootDomainId))
    setPurchaseState(PurchaseSubscriptionState.purchase)
  }

  const localizationContext = useContext(LocalizationContext)

  const stripePromise = useMemo(
    () =>
      loadStripe(STRIPE_PK || '', {
        locale: elementsLocaleMapper(localizationContext.locale),
      }),
    [localizationContext.locale]
  )

  const paymentMethodsLoaded = useAppSelector(
    paymentMethodsListSelector.isFinished
  )

  const isLoadingSetupIntent = useAppSelector(setupIntentSelector.isFetching)
  const isPriceLoaded = useAppSelector(subscriptionPricesSelector.isFinished)
  const loaded =
    paymentMethodsLoaded && !isLoadingSetupIntent && isPriceLoaded && apPrice

  useEffect(() => {
    if (paymentMethodsLoaded && rootDomainId) {
      dispatch(createSetupIntentThunk(rootDomainId))
    }
  }, [dispatch, paymentMethodsLoaded, rootDomainId])

  const isNextDisabled = useMemo(() => {
    switch (purchaseState) {
      case PurchaseSubscriptionState.addDevice: {
        return !apEnabled || !networkAiEnabled
      }
      case PurchaseSubscriptionState.purchase: {
        return (
          !isAcceptPrivacy ||
          isPurchaseRequestLoading ||
          isPaymentMethodDetaching
        )
      }
      case PurchaseSubscriptionState.addPaymentMethod: {
        return true
      }
      case PurchaseSubscriptionState.error: {
        return true
      }
      default: {
        return false
      }
    }
  }, [
    networkAiEnabled,
    purchaseState,
    isAcceptPrivacy,
    apEnabled,
    isPaymentMethodDetaching,
    isPurchaseRequestLoading,
  ])

  /*
  const handlePreviewSubscription = useCallback(async () => {
    try {
      if (!apPrice) {
        throw new Error('No price selected')
      }

      setIsShowLoader(true)

      const fetchPreviewPrice = await dispatch(
        previewSubscriptionThunk({
          homeId,
          rootDomainId,
          type: 'company',
          couponId: upcomingInvoice.discount?.coupon.id || '',
          items: [
            {
              priceId: apPrice.id,
              quantity: apQuantity,
            },
          ],
        })
      ).then(unwrapResult)
      
      setPreviewPrice(fetchPreviewPrice)
      return fetchPreviewPrice

    } catch (error) {
      console.log('Error while previewing a subscription', error)
      return {amount_due: 0}
    } finally {
      setIsShowLoader(false)
    }
  }, [
    apPrice,
    apQuantity,
    upcomingInvoice,
    dispatch,
    homeId,
    rootDomainId,
  ])
*/

  const handlePurchaseSubscription = useCallback(async () => {
    try {
      // TODO remove throws and add a proper validation
      if (!selectedPaymentMethod) {
        throw new Error('No payment method selected')
      }
      if (!apPrice) {
        throw new Error('No price selected')
      }

      setIsShowLoader(true)
      await dispatch(
        purchaseSubscriptionThunk({
          paymentMethodId: selectedPaymentMethod,
          homeId,
          rootDomainId,
          type: 'company',
          couponId: upcomingInvoice.discount?.coupon.id || '',
          items: [
            {
              priceId: apPrice.id,
              quantity: apQuantity,
            },
          ],
        })
      ).then(unwrapResult)

      siteId && (await dispatch(fetchClaimDevicesThunk(siteId)))
      await dispatch(fetchHomeThunk(homeId))

      setPurchaseState(PurchaseSubscriptionState.success)
    } catch (error) {
      setPurchaseState(PurchaseSubscriptionState.error)
      console.log('Error while purchasing a subscription', error)
    } finally {
      setIsShowLoader(false)
    }
  }, [
    apPrice,
    apQuantity,
    upcomingInvoice,
    selectedPaymentMethod,
    dispatch,
    homeId,
    rootDomainId,
    siteId,
  ])

  const bottomBlockRightButtonText = useMemo(() => {
    switch (purchaseState) {
      case PurchaseSubscriptionState.purchase:
        return purchaseSubscriptionMessages.purchaseAndSubscribeTitle
      case PurchaseSubscriptionState.addDevice:
        return purchaseSubscriptionMessages.purchaseTitle
      case PurchaseSubscriptionState.addPaymentMethod:
        return commonMessages.save
      default:
        return commonMessages.next
    }
  }, [purchaseState])

  const bottomBlockLeftButtonText = useMemo(() => {
    switch (purchaseState) {
      case PurchaseSubscriptionState.success:
        return commonMessages.close
      default:
        return commonMessages.back
    }
  }, [purchaseState])

  const wizardPageTitle = useMemo(() => {
    switch (purchaseState) {
      case PurchaseSubscriptionState.purchase:
        return purchaseSubscriptionMessages.checkoutTitle
      case PurchaseSubscriptionState.addPaymentMethod:
        return purchaseSubscriptionMessages.addPaymentMethodTitle
      default:
        return purchaseSubscriptionMessages.purchaseSubscriptionTitle
    }
  }, [purchaseState])

  const handleNext = useCallback(async () => {
    switch (purchaseState) {
      case PurchaseSubscriptionState.addDevice: {
        hasPaymentMethods
          ? setPurchaseState(PurchaseSubscriptionState.purchase)
          : setPurchaseState(PurchaseSubscriptionState.addPaymentMethod)
        break
      }
      case PurchaseSubscriptionState.addPaymentMethod: {
        await paymentMethodFormRef.current?.submitForm()
        if (paymentMethodFormRef.current?.isValid) {
          setPurchaseState(PurchaseSubscriptionState.purchase)
        }
        break
      }
      case PurchaseSubscriptionState.purchase: {
        await handlePurchaseSubscription()
        break
      }
      case PurchaseSubscriptionState.success: {
        const path = generatePath(ROUTES.networkAi.wizard.claimDevice, {
          rootDomainId,
          jobId,
          homeId,
        })

        history.push(path)
        break
      }
      case PurchaseSubscriptionState.error: {
        break
      }
      default: {
        setPurchaseState(PurchaseSubscriptionState.addDevice)
        setPrevPurchaseState(PurchaseSubscriptionState.addDevice)
        break
      }
    }
  }, [
    purchaseState,
    hasPaymentMethods,
    handlePurchaseSubscription,
    rootDomainId,
    jobId,
    homeId,
    history,
  ])

  const handleCancel = useCallback(() => {
    switch (purchaseState) {
      case PurchaseSubscriptionState.addDevice: {
        const path = generatePath(ROUTES.networkAi.wizard.addNetwork, {
          rootDomainId,
          jobId,
          homeId,
        })
        history.push(path)
        break
      }
      case PurchaseSubscriptionState.addPaymentMethod: {
        setPurchaseState(prevPurchaseState)
        break
      }
      case PurchaseSubscriptionState.purchase: {
        setPurchaseState(PurchaseSubscriptionState.addDevice)
        break
      }
      case PurchaseSubscriptionState.success: {
        const path = generatePath(ROUTES.networkAi.dashboard.root, {
          rootDomainId,
          jobId,
          homeId,
        })
        history.push(path)
        setPurchaseState(PurchaseSubscriptionState.addDevice)
        break
      }
      case PurchaseSubscriptionState.error: {
        setPurchaseState(PurchaseSubscriptionState.addDevice)
        break
      }
      default: {
        setPurchaseState(PurchaseSubscriptionState.addDevice)
        break
      }
    }
  }, [purchaseState, rootDomainId, jobId, homeId, history, prevPurchaseState])

  const bottomBlock = useMemo(() => {
    return (
      <NetworkWizardBottomPanel>
        <PurchaseStepBottomPanelContainer>
          {!loaded || isShowLoader ? (
            <SpinnerCentered size="40" />
          ) : (
            <>
              {purchaseState === 2 && (
                <StyledTotalToPay>
                  <div>
                    Pay {formatPrice(amountDue)} Today and Subscribe for&nbsp;
                  </div>
                  <div>
                    {totalAmountDue && formatPrice(totalAmountDue)}/year
                  </div>
                </StyledTotalToPay>
              )}

              <PurchaseStepBottomPanelBtns>
                <NetworkWizardCancelButton
                  variant="secondary"
                  type="button"
                  onClick={handleCancel}
                >
                  {formatMessage(bottomBlockLeftButtonText)}
                </NetworkWizardCancelButton>

                {purchaseState !==
                  PurchaseSubscriptionState.addPaymentMethod && (
                  <NetworkWizardSubmitButton
                    variant="primary"
                    type="submit"
                    disabled={isNextDisabled}
                    onClick={handleNext}
                  >
                    {formatMessage(bottomBlockRightButtonText)}
                  </NetworkWizardSubmitButton>
                )}
              </PurchaseStepBottomPanelBtns>
            </>
          )}
        </PurchaseStepBottomPanelContainer>
      </NetworkWizardBottomPanel>
    )
  }, [
    isShowLoader,
    loaded,
    formatMessage,
    handleCancel,
    bottomBlockLeftButtonText,
    isNextDisabled,
    handleNext,
    bottomBlockRightButtonText,
    purchaseState,
    amountDue,
    totalAmountDue,
  ])

  const accessPointsCount = useMemo(() => {
    const apItem = purchaseRequestData?.items?.data?.find(
      (x: any) => x.price.nickname === 'NAI - Access Points'
    )
    return apItem?.quantity
  }, [purchaseRequestData])

  useEffect(() => {
    if (
      paymentMethodsLoaded &&
      !hasPaymentMethods &&
      purchaseState === PurchaseSubscriptionState.purchase
    ) {
      setPurchaseState(PurchaseSubscriptionState.addPaymentMethod)
    }
  }, [hasPaymentMethods, paymentMethodsLoaded, purchaseState])

  /*
  useEffect(() => {
    if (purchaseState > 0) {
      handlePreviewSubscription()
    }
  }, [  
    purchaseState,  
  handlePreviewSubscription])
*/

  useEffect(() => {
    let isMounted = true
    let total = 0

    if (isMounted) {
      total = apQuantity * (apPrice?.unit_amount || 0)

      if (essentialsPrice !== undefined && subscriptionState === 'ENROLLED') {
        setTotalAmountDue(total + essentialsPrice?.unit_amount || 0)
      } else {
        setTotalAmountDue(total)
      }
    }

    return () => {
      isMounted = false
    }
  }, [
    essentialsPrice,
    setTotalAmountDue,
    apPrice,
    apQuantity,
    subscriptionState,
  ])

  return (
    <NetworkWizardLayout
      bottomBlock={bottomBlock}
      currentWizardStepId={isSuccessSubcription ? 2.5 : 2}
    >
      <NetworkWizardTitle>{formatMessage(wizardPageTitle)}</NetworkWizardTitle>
      <PurchaseSubscriptionMainBlock>
        {isSuccessSubcription && (
          <PurchaseStateSuccess
            nextBillingDate={purchaseRequestData?.current_period_end * 1000}
            accessPointsCount={accessPointsCount || 0}
            accessPointsRenewCount={apQuantity || 0}
            jobName={job?.name}
          />
        )}
        {purchaseState === PurchaseSubscriptionState.error && (
          <PurchaseState
            icon={mdiAlert}
            iconColor={theme.colors.error}
            title={formatMessage(commonMessages.error)}
          >
            Purchase subscription failed. Please try again later.
          </PurchaseState>
        )}
        {purchaseState === PurchaseSubscriptionState.purchase && (
          <CheckoutBlockContainer>
            <StyledListCardWrapper>
              {isPaymentMethodDetaching || !paymentMethodsLoaded ? (
                <SpinnerCentered size="40" />
              ) : (
                <>
                  <SectionLabel>
                    <span>Payment Method</span>
                    {
                      <CommonLinkButton
                        onClick={() => {
                          setPurchaseState(
                            PurchaseSubscriptionState.addPaymentMethod
                          )
                          setPrevPurchaseState(
                            PurchaseSubscriptionState.purchase
                          )
                        }}
                      >
                        <MdiIcon path={mdiPlus} />
                      </CommonLinkButton>
                    }
                  </SectionLabel>
                  <PaymentMethodsCardsList />
                </>
              )}
            </StyledListCardWrapper>
            <CheckoutBlockWrapper>
              <CheckoutInfo
                setIsAcceptPrivacy={setIsAcceptPrivacy}
                networkPrice={apPrice}
                quantity={apQuantity}
                isShowEssentialsPlan={false}
                setAmountDue={setAmountDue}
              />
            </CheckoutBlockWrapper>
          </CheckoutBlockContainer>
        )}
        {isAddPaymentMethodState && (
          <PurchaseSubscriptionBlock>
            <StyledAddCardWrapper>
              <WhiteBlock>
                <Elements
                  key={localizationContext.locale}
                  stripe={stripePromise}
                >
                  <AddPaymentMethodForm
                    isFirstMethod={!hasPaymentMethods}
                    onSuccess={handlePaymentMethodAdd}
                    formRef={paymentMethodFormRef}
                    isStandalone
                  />
                </Elements>
              </WhiteBlock>
            </StyledAddCardWrapper>
          </PurchaseSubscriptionBlock>
        )}
        {purchaseState === PurchaseSubscriptionState.addDevice && (
          <AddDeviceStep
            apEnabled={apEnabled}
            apQuantity={apQuantity}
            amount={apPrice?.unit_amount ?? 0}
            setApEnabled={setApEnabled}
            setApQuantity={setApQuantity}
            networkAiEnabled={networkAiEnabled}
            setNetworkAiEnabled={setNetworkAiEnabled}
          />
        )}
        {isShowTotal && (
          <>
            {creditAmountsLoading ? (
              <SpinnerCentered size="40" />
            ) : (
              <PurchaseDeviceTotal
                apQuantity={apQuantity}
                apPrice={apPrice?.unit_amount ?? 0}
                essentialsPrice={essentialsPrice?.unit_amount || 0}
                totalPrice={totalAmountDue}
                renewalDate={renewalDate[1]}
                subscriptionState={subscriptionState}
                essentialsCredits={essentialsCredits}
                apCredits={apCredits}
              />
            )}
          </>
        )}
      </PurchaseSubscriptionMainBlock>
    </NetworkWizardLayout>
  )
}

export default PurchaseDeviceSubscription
